import { useEffect, useRef, useState } from "react";
import { Camera, Play, SkipForward, Settings2 } from "lucide-react";

import { useReplayPreflight } from "@/hooks/useReplayPreflight";
import { useReplayStore } from "@/stores/replayStore";
import { useDrawingStore } from "@/stores/drawingStore";
import { useUIStore } from "@/stores/uiStore";
import { triggerSnapshot } from "@/lib/share";

interface ContextMenuPosition {
  x: number;
  y: number;
  timestamp: number;
  price: number | null;
}

interface ReplayContextMenuProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  chartRef: React.RefObject<unknown>;
}

type ChartInstance = {
  convertFromPixel?: (params: { x: number; y: number }) => { dataIndex: number; timestamp?: number; value?: number } | null;
  getDataList?: () => Array<{ timestamp: number }>;
  scrollToRealTime?: (rightSpace?: number) => void;
};

export function ReplayContextMenu({ containerRef, chartRef }: ReplayContextMenuProps) {
  const [position, setPosition] = useState<ContextMenuPosition | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const replaySessionState = useReplayStore((s) => s.sessionState);
  const { startPreflight } = useReplayPreflight();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const getChart = () => chartRef.current as ChartInstance | null;

    const findDataAtPixel = (x: number, y: number): { timestamp: number | null; price: number | null } => {
      const chart = getChart();
      if (!chart?.convertFromPixel) return { timestamp: null, price: null };
      try {
        const result = chart.convertFromPixel({ x, y });
        if (!result) return { timestamp: null, price: null };

        let timestamp: number | null = result.timestamp ?? null;
        if (timestamp == null && result.dataIndex != null && chart.getDataList) {
          timestamp = chart.getDataList()[result.dataIndex]?.timestamp ?? null;
        }
        const price = result.value ?? null;
        return { timestamp, price };
      } catch {
        return { timestamp: null, price: null };
      }
    };

    const openMenu = (clientX: number, clientY: number, rect: DOMRect) => {
      // Skip if a drawing tool is active (capture-phase interceptor handles this, but guard here too)
      if (useDrawingStore.getState().activeTool) return;

      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const { timestamp, price } = findDataAtPixel(x, y);

      if (timestamp != null) {
        setPosition({ x: clientX, y: clientY, timestamp, price });
      }
    };

    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault();
      const rect = container.getBoundingClientRect();
      openMenu(event.clientX, event.clientY, rect);
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      longPressTimerRef.current = setTimeout(() => {
        const rect = container.getBoundingClientRect();
        openMenu(touch.clientX, touch.clientY, rect);
      }, 500);
    };

    const handleTouchEnd = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };

    const handleTouchMove = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setPosition(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPosition(null);
    };

    container.addEventListener("contextmenu", handleContextMenu);
    container.addEventListener("touchstart", handleTouchStart, { passive: true });
    container.addEventListener("touchend", handleTouchEnd);
    container.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      container.removeEventListener("contextmenu", handleContextMenu);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchend", handleTouchEnd);
      container.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, [containerRef, chartRef]);

  const handleReplayFromHere = () => {
    if (!position) return;
    void startPreflight(position.timestamp, Date.now());
    setPosition(null);
  };

  const handleGoToLatest = () => {
    const chart = chartRef.current as ChartInstance | null;
    chart?.scrollToRealTime?.(80);
    setPosition(null);
  };

  const handleSaveImage = () => {
    setPosition(null);
    triggerSnapshot();
  };

  const handleSettings = () => {
    setPosition(null);
    useUIStore.getState().openSettings();
  };

  const isLive = replaySessionState === "live";

  const menuStyle: React.CSSProperties = {
    position: "fixed",
    left: Math.min(position?.x ?? 0, window.innerWidth - 200),
    top: Math.min((position?.y ?? 0) - 8, window.innerHeight - 240),
    zIndex: 9999,
  };

  return (
    <>
      {position && (
        <div ref={menuRef} style={menuStyle}>
          <div
            className="py-1 min-w-[180px] flex flex-col"
            style={{
              backgroundColor: "var(--bg-elevated)",
              border: "1px solid var(--lyt-border-subtle)",
              borderRadius: "var(--radius-lg, 10px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
            }}
          >
            {/* Chart actions */}
            <MenuItem icon={<SkipForward className="size-3.5" />} onClick={handleGoToLatest}>
              Go to latest
            </MenuItem>
            <MenuItem icon={<Camera className="size-3.5" />} onClick={handleSaveImage}>
              Save as image
            </MenuItem>
            <MenuItem icon={<Settings2 className="size-3.5" />} onClick={handleSettings}>
              Settings
            </MenuItem>

            {/* Replay — only when live */}
            {isLive && (
              <>
                <div
                  className="my-0.5 h-px"
                  style={{ backgroundColor: "var(--lyt-border-subtle)" }}
                />
                <MenuItem
                  icon={<Play className="size-3.5" style={{ color: "var(--lyt-accent)" }} />}
                  onClick={handleReplayFromHere}
                  accent
                >
                  Replay from here
                </MenuItem>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function MenuItem({
  icon,
  children,
  onClick,
  accent,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onClick: () => void;
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2.5 w-full px-3 py-2 text-xs transition-colors text-left"
      style={{
        color: accent ? "var(--lyt-accent)" : "var(--text-primary)",
        backgroundColor: "transparent",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = "var(--bg-surface-hover, rgba(255,255,255,0.06))";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
      }}
    >
      <span style={{ color: accent ? "var(--lyt-accent)" : "var(--text-secondary)", flexShrink: 0 }}>
        {icon}
      </span>
      {children}
    </button>
  );
}
